import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Users } from 'lucide-react';
import Icon from 'shared/components/Icon';

const ConnectorDropdown = ({ value, isFirst, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({ top: rect.bottom + scrollY + 4, left: rect.left + scrollX, width: rect.width });
    }
  }, [isOpen]);

  if (isFirst) {
    return <span className="text-xs font-medium text-[color:var(--text-muted)] px-2">WHERE</span>;
  }

  const operators = [
    { value: 'AND', label: 'AND' },
    { value: 'OR', label: 'OR' },
  ];
  const selected = operators.find((op) => op.value === value) || operators[0];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((o) => !o);
        }}
        className="px-2.5 py-1.5 text-xs bg-[color:var(--border-subtle)] rounded-lg text-center text-[color:var(--text-primary)] focus:outline-none focus:ring-0 cursor-pointer hover:bg-[color:var(--border-subtle)] transition-colors"
        style={{ minWidth: '60px' }}
      >
        {selected.label}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              className="fixed z-[9999] bg-surface border border-line-strong rounded shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${Math.max(position.width || 120, 120)}px`,
              }}
            >
              <div className="py-1">
                {operators.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => {
                      onChange(op.value);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-[color:var(--surface-sunken)] flex items-center ${value === op.value ? 'bg-[color:var(--border-subtle)] text-[color:var(--text-primary)]' : ''}`}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/**
 * Cohort membership row for group WHERE clause (no value editing).
 */
const GroupCohortChip = ({ filter, isFirst, onEdit, onRemove, onUpdate }) => {
  const connector = filter.connector || (isFirst ? 'WHERE' : 'AND');

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <ConnectorDropdown value={connector} isFirst={isFirst} onChange={(c) => onUpdate({ connector: c })} />
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit();
          }
        }}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-[color:var(--border-subtle)] rounded-lg cursor-pointer hover:bg-[color:var(--border-subtle)] transition-colors"
      >
        <Users className="h-4 w-4 text-[color:var(--text-muted)] flex-shrink-0" />
        <span
          className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-[color:var(--border-subtle)] text-[color:var(--text-muted)] flex-shrink-0"
          title="Cohort"
        >
          C
        </span>
        <span className="text-sm text-[color:var(--text-primary)] font-medium">{filter.propertyLabel || 'Cohort'}</span>
      </div>
      <span className="text-xs text-[color:var(--text-muted)]">is in cohort</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-1.5 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)] transition-colors"
        title="Delete filter"
      >
        <Icon name="delete" size={16} />
      </button>
    </div>
  );
};

export default GroupCohortChip;
